import React from 'react';

const RecurringExpenses: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Recurring Expenses Management</h1>
        <p className="text-gray-600">
          Automate regular expense entries with scheduled recurring expense profiles for consistent financial tracking
        </p>
      </div>

      {/* Recurring Expenses Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recurring Expenses Overview</h2>
        <p className="text-gray-600 mb-4">
          The Recurring Expenses module in DAS allows you to automate regular expense entries by creating profiles
          that generate expenses automatically based on your specified schedule. This helps maintain consistent
          financial tracking for regular payments like rent, utilities, subscriptions, and other periodic expenses.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/list.png"
            alt="Recurring Expenses List View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Automated expense generation</li>
              <li>Flexible scheduling options</li>
              <li>Profile name identification</li>
              <li>Status management (Active/Stopped)</li>
              <li>Start and end date control</li>
              <li>Amount and frequency tracking</li>
              <li>Supplier and customer linking</li>
              <li>Real-time search and filtering</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Schedule Options</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Daily:</strong> Every day recurring</li>
              <li><strong>Weekly:</strong> Every week recurring</li>
              <li><strong>Bi-weekly:</strong> Every 2 weeks</li>
              <li><strong>Monthly:</strong> Every month recurring</li>
              <li><strong>Quarterly:</strong> Every 3 months</li>
              <li><strong>Semi-annual:</strong> Every 6 months</li>
              <li><strong>Annual:</strong> Every year recurring</li>
              <li><strong>Custom:</strong> Flexible day intervals</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Creating New Recurring Expenses */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Recurring Expenses</h2>
        <p className="text-gray-600 mb-4">
          Set up automated expense profiles with comprehensive scheduling and configuration options.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/form.png"
            alt="New Recurring Expense Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Profile Configuration</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Basic Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Profile Name:</strong> Descriptive name for the recurring expense (required)</li>
              <li><strong>Branch:</strong> Organizational branch assignment</li>
              <li><strong>Expense Account:</strong> Chart of accounts classification</li>
              <li><strong>Paid Through:</strong> Payment method or account</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Financial Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Amount:</strong> Fixed amount for each occurrence</li>
              <li><strong>Currency:</strong> Transaction currency</li>
              <li><strong>Tax:</strong> Applicable tax rates</li>
              <li><strong>Bank Charges:</strong> Additional charges if any</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Schedule Configuration</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Frequency Settings:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Repeat Every:</strong> Choose from predefined intervals or custom days</li>
              <li><strong>Start Date:</strong> When the recurring expense begins</li>
              <li><strong>Ends On:</strong> Specific end date or "Never Expires"</li>
              <li><strong>Next Expense Date:</strong> Automatically calculated</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Entity Relationships:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Supplier:</strong> Link to vendor (optional)</li>
              <li><strong>Customer:</strong> Associate with customer if applicable</li>
              <li><strong>Notes:</strong> Additional information and context</li>
              <li><strong>Search Icons:</strong> Quick lookup for suppliers and customers</li>
            </ul>
          </div>
        </div>

        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/repeat-every.png"
            alt="Recurring Expense Repeat Options"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Repeat Frequency Options</h3>
        <p className="text-gray-600 mb-4">
          The system provides flexible scheduling options to accommodate different business needs:
        </p>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Standard Intervals</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Week (7 days)</li>
              <li>• 2 Weeks (14 days)</li>
              <li>• Month (30 days)</li>
              <li>• 2 Months (60 days)</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Extended Periods</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 3 Months (90 days)</li>
              <li>• 6 Months (180 days)</li>
              <li>• Year (365 days)</li>
              <li>• 2 Years (730 days)</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Custom Options</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Custom day intervals</li>
              <li>• Specific calendar dates</li>
              <li>• Business day only options</li>
              <li>• Holiday adjustments</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Planning Tip:</strong> Choose the frequency that best matches your actual expense pattern.
            For utilities, monthly is common, while for rent payments, you might use monthly or quarterly
            depending on your lease terms.
          </p>
        </div>
      </div>

      {/* Editing Recurring Expenses */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Editing Recurring Expenses</h2>
        <p className="text-gray-600 mb-4">
          Modify existing recurring expense profiles to update schedules, amounts, or other configuration details.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/edit.png"
            alt="Edit Recurring Expense Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Editable Fields</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Profile Updates:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Change profile name and description</li>
              <li>Update expense amount and currency</li>
              <li>Modify payment method or account</li>
              <li>Adjust branch assignment</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Schedule Modifications:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Change recurring frequency</li>
              <li>Update start and end dates</li>
              <li>Modify supplier or customer associations</li>
              <li>Add or update notes and documentation</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Custom Frequency Configuration</h3>
        <p className="text-gray-600 mb-4">
          The edit form shows custom frequency options when "Custom" is selected, allowing precise control over timing:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Day Intervals:</strong> Specify exact number of days between occurrences</li>
          <li><strong>Date Selection:</strong> Set specific start and end dates with calendar picker</li>
          <li><strong>Never Expires Option:</strong> For ongoing recurring expenses</li>
          <li><strong>Entity Associations:</strong> Link or unlink suppliers and customers as needed</li>
        </ul>
      </div>

      {/* Recurring Expense Status Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Management and Control</h2>
        <p className="text-gray-600 mb-4">
          Manage recurring expense profiles with status controls to start, stop, or resume automated expense generation.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/detail.png"
            alt="Recurring Expense Detail View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Status Options</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Active Status:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Automatically generates expenses on schedule</li>
              <li>Next expense date is calculated and displayed</li>
              <li>Can be stopped at any time</li>
              <li>Status shown as "Active" in list view</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Stopped Status:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>No automatic expense generation</li>
              <li>Profile preserved for future reactivation</li>
              <li>Can be resumed with updated schedule</li>
              <li>Status shown as "Stopped" in list view</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons and Controls</h3>
        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/more-button.png"
            alt="Recurring Expense More Actions"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Primary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Edit Button:</strong> Modify profile settings and schedule</li>
              <li><strong>More Actions (⋮):</strong> Access additional management options</li>
              <li><strong>History Button:</strong> View complete expense generation history</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>More Actions Menu:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Resume:</strong> Restart stopped recurring expenses</li>
              <li><strong>Delete:</strong> Permanently remove the recurring profile</li>
              <li><strong>Duplicate:</strong> Create similar recurring expense profiles</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Generated Expenses View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Generated Expenses Tracking</h2>
        <p className="text-gray-600 mb-4">
          Monitor all expenses generated by recurring profiles with detailed tracking and individual expense management.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/recurring-expenses/detail-all-expense.png"
            alt="All Generated Expenses View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Expense Tracking Features</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Generated Expense Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Date of each generated expense</li>
              <li>Expense account classification</li>
              <li>Payment method used</li>
              <li>Amount for each occurrence</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Management Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Individual expense deletion</li>
              <li>Edit specific generated expenses</li>
              <li>View expense details and attachments</li>
              <li>Track payment status and reconciliation</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Overview and All Expenses Tabs</h3>
        <p className="text-gray-600 mb-4">
          The detail view provides two comprehensive tabs for managing recurring expense information:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Overview Tab</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Profile summary and configuration</li>
              <li>• Current status and next expense date</li>
              <li>• Financial details and amounts</li>
              <li>• Associated entities (supplier/customer)</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">All Expenses Tab</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Complete list of generated expenses</li>
              <li>• Individual expense management</li>
              <li>• Delete specific occurrences</li>
              <li>• Track payment history</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Search and Filtering */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Search and Filtering</h2>
        <p className="text-gray-600 mb-4">
          Efficiently locate and manage recurring expense profiles using powerful search and filtering capabilities.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
        <p className="text-gray-600 mb-4">
          Use the search icon (🔍) in the top-right corner for instant recurring expense filtering:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Real-time Search:</strong> Filter profiles as you type</li>
          <li><strong>Multi-field Search:</strong> Searches across profile names, expense accounts, suppliers, and customers</li>
          <li><strong>Status Filtering:</strong> Find active or stopped profiles quickly</li>
          <li><strong>Amount Filtering:</strong> Search by expense amounts</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Use Cases</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Common Searches:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Find all parking-related recurring expenses</li>
              <li>Locate stopped profiles for reactivation</li>
              <li>Search by specific supplier or customer</li>
              <li>Filter by expense account categories</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Management Benefits:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Quick profile identification and access</li>
              <li>Efficient status management</li>
              <li>Streamlined expense automation oversight</li>
              <li>Better financial planning and forecasting</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
};

export default RecurringExpenses;
